import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { ArrowLeft, Check, ChevronDown, SearchSm } from '@untitled-ui/icons-react';
import { useEffect, useMemo, useState } from 'react';

import { Badge } from '../../../../components/badge';
import { Button } from '../../../../components/buttons/button';
import { IconButton } from '../../../../components/buttons/icon-button';
import { DropdownMenu } from '../../../../components/dropdown-menu';
import { TextField } from '../../../../components/inputs/text-field';
import { DropdownMenuOption } from '../../../../components/menu-option';
import { MobileBlocker } from '../../../../components/mobile-blocker';
import { Pagination } from '../../../../components/pagination';
import { type Product, type ProductDisplayStatus } from '../../../../entity/api/product';
import { type ProductSortAndFilter } from '../../../../entity/api/routes/product';
import { trackError } from '../../../../error/tracking';
import { captureException } from '../../../../logger/capture-exception';
import { exportBulkGroupToCsv } from '../../../../sdk/bulk-group';
import {
  useBulkGroupById,
  useProductsByBulkGroupId,
  useRemoveProductBulk,
  useUpdateProduct,
  useUpdateProductBulk,
  useUsersByIds,
} from '../../../../services/storage';
import { tGlobal, tNamespace } from '../../../../services/translation';
import { getCountryName } from '../../../../shared/countries';
import { Feature, isFeatureAvailable } from '../../../../shared/feature';
import { useDebouncedEffect } from '../../../../shared/hooks/use-debounce';
import { SingleProductListing } from '../components/-product-listing';

const t = tNamespace('app.products.bulk-import');
const PRODUCTS_PER_PAGE = 20;

type BulkImportSearch = {
  page?: number;
  search?: string;
  sortBy?: 'createdAt' | 'updatedAt';
  sortByDir?: 'ASC' | 'DESC';
};

export const Route = createFileRoute('/app/_sidebar-layout/products/bulk-import/$id')({
  validateSearch: function (search: Record<string, unknown>): BulkImportSearch {
    let validatedPage = 1;
    if (typeof search.page === 'number') {
      const pageNum = Math.floor(search.page);
      validatedPage = Math.max(pageNum, 1);
    }

    return {
      page: validatedPage,
      search: typeof search.search === 'string' ? search.search : undefined,
      sortBy:
        search.sortBy === 'createdAt' || search.sortBy === 'updatedAt' ? search.sortBy : undefined,
      sortByDir: search.sortByDir === 'ASC' ? 'ASC' : 'DESC',
    };
  },
  // eslint-disable-next-line no-use-before-define
  component: RouteComponent,
});

function RouteComponent() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const search = Route.useSearch();

  // URL is the source of truth
  const page = search.page ?? 1;
  const searchTerm = search.search ?? '';
  const sortBy = search.sortBy ?? 'updatedAt';
  const sortByDir = search.sortByDir ?? 'DESC';

  // Only local state: input typing UX
  const [searchInput, setSearchInput] = useState(searchTerm);

  // Build query params from URL only
  const queryParams: ProductSortAndFilter = useMemo(
    function () {
      return {
        offset: (page - 1) * PRODUCTS_PER_PAGE,
        limit: PRODUCTS_PER_PAGE,
        search: searchTerm.length > 0 ? searchTerm : undefined,
        sortBy: sortBy === 'createdAt' ? 'createdAt' : 'updatedAt',
        sortByDir,
      };
    },
    [page, searchTerm, sortBy, sortByDir]
  );

  // TanStack Query for bulk group and products
  const { data: bulkGroup } = useBulkGroupById(id);
  const productsQuery = useProductsByBulkGroupId(id, queryParams);
  const products = useMemo(
    function () {
      return productsQuery.data?.products ?? [];
    },
    [productsQuery.data?.products]
  );
  const totalCount = productsQuery.data?.totalCount ?? 0;

  // TanStack Query mutations
  const updateProductMutation = useUpdateProduct();
  const updateProductBulkMutation = useUpdateProductBulk();
  const deleteProductBulkMutation = useRemoveProductBulk();

  // Extract unique user IDs from products for author fetching
  const productAuthorIds = useMemo(
    function () {
      const ids = products.map((p) => p.createdBy).filter((id): id is string => id != null);
      return [...new Set(ids)]; // deduplicate
    },
    [products]
  );

  // TanStack Query hook for fetching authors by IDs
  const { data: authorsData } = useUsersByIds(productAuthorIds);
  const authorsList = authorsData ?? [];

  const [loadingExport, setLoadingExport] = useState(false);

  const totalPages = Math.max(1, Math.ceil(totalCount / PRODUCTS_PER_PAGE));

  const SortByDropdownMenuItems = [
    {
      value: 'DESC',
      label: tGlobal('mostRecent'),
      onClick: function () {
        // eslint-disable-next-line no-use-before-define
        handleSortDirChange('DESC');
      },
    },
    {
      value: 'ASC',
      label: tGlobal('oldestFirst'),
      onClick: function () {
        // eslint-disable-next-line no-use-before-define
        handleSortDirChange('ASC');
      },
    },
  ];

  function handleSortDirChange(dir: 'ASC' | 'DESC') {
    navigate({
      from: Route.fullPath,
      search: function (prev) {
        return {
          ...prev,
          sortByDir: dir,
          page: 1,
        };
      },
    }).catch(function (err) {
      captureException(
        err,
        {
          file: 'app/_sidebar-layout.products/bulk-import/$id.tsx',
          method: 'handleSortDirChange',
          infoMessage: 'Failed to update sortByDir',
        },
        trackError
      );
    });
  }

  function handlePageChange(page: number) {
    navigate({
      from: Route.fullPath,
      search: function (prev) {
        return { ...prev, page };
      },
    }).catch(function (err) {
      captureException(
        err,
        {
          file: 'app/_sidebar-layout.products/bulk-import/$id.tsx',
          method: 'handlePageChange',
          infoMessage: 'Failed to navigate to page',
        },
        trackError
      );
    });
  }

  function handleUpdateInBulk(productsToUpdate: Product[]) {
    updateProductBulkMutation.mutate(
      { products: productsToUpdate },
      {
        onError: function (err) {
          captureException(
            err,
            {
              file: 'app/_sidebar-layout.products/bulk-import/$id.tsx',
              method: 'handleUpdateInBulk',
              infoMessage: 'unable to update products',
            },
            trackError
          );
        },
      }
    );
  }

  function updateProductStatus(productId: string, status: ProductDisplayStatus) {
    const product = products.find(function (p) {
      return p._id === productId;
    });
    if (product == null) {
      return;
    }
    if (status === product.displayStatus) {
      return;
    }
    const updatedProduct = {
      ...product,
      displayStatus: status,
    };
    updateProductMutation.mutate(
      { product: updatedProduct },
      {
        onError: function (err) {
          captureException(
            err,
            {
              file: 'app/_sidebar-layout.products/bulk-import/$id.tsx',
              method: 'updateProductStatus',
              infoMessage: 'unable to update product status',
            },
            trackError
          );
        },
      }
    );
  }

  function handleDeleteSelected(productIds: string[]) {
    if (productIds.length === 0) {
      return;
    }
    deleteProductBulkMutation.mutate(productIds, {
      onError: function (err) {
        captureException(
          err,
          {
            file: 'app/_sidebar-layout.products/bulk-import/$id.tsx',
            method: 'handleDeleteSelected',
            infoMessage: 'unable to delete products',
          },
          trackError
        );
      },
    });
  }

  useEffect(
    function () {
      setSearchInput(searchTerm);
    },
    [searchTerm]
  );

  useDebouncedEffect(
    function () {
      if (searchInput === searchTerm) {
        return;
      }

      navigate({
        from: Route.fullPath,
        search: function (prev) {
          return {
            ...prev,
            search: searchInput.trim().length > 0 ? searchInput.trim() : undefined,
            page: 1,
          };
        },
      }).catch(function (err) {
        captureException(
          err,
          {
            file: 'app/_sidebar-layout.products/bulk-import/$id.tsx',
            method: 'useDebouncedEffect[searchInput]',
            infoMessage: 'Failed to update search in URL',
          },
          trackError
        );
      });
    },
    [searchInput, searchTerm, navigate],
    500
  );

  useEffect(
    function () {
      if (productsQuery.isLoading) {
        return;
      }
      if (page < 1 || page > totalPages) {
        navigate({
          from: Route.fullPath,
          search: function (prev) {
            return {
              ...prev,
              page: Math.min(Math.max(page, 1), totalPages),
            };
          },
          replace: true,
        }).catch(function (err) {
          captureException(
            err,
            {
              file: 'app/_sidebar-layout.products/bulk-import/$id.tsx',
              method: 'useEffect[pageValidation]',
              infoMessage: 'Failed to redirect to valid page',
            },
            trackError
          );
        });
      }
    },
    [page, totalPages, productsQuery.isLoading, navigate]
  );

  useEffect(
    function () {
      if (!isFeatureAvailable(Feature.PRODUCT)) {
        navigate({ to: '/app/blogs' }).catch(function (err) {
          captureException(
            err,
            {
              file: 'src/routes/app/_sidebar-layout.products/bulk-import/$id.tsx',
              method: 'navigate',
              infoMessage: 'unable to navigate to /app/blogs',
            },
            trackError
          );
        });
      }
    },
    [navigate]
  );

  return (
    <>
      <MobileBlocker />
      <div className="h-full w-full flex-1 overflow-y-auto">
        <div className="flex items-center justify-between border-b border-gray-200 p-4">
          <div className="flex items-center gap-2">
            <IconButton
              variant="outline"
              size="sm"
              icon={ArrowLeft}
              onClick={function () {
                navigate({ to: '/app/products/bulk-import' }).catch(function (err) {
                  captureException(
                    err,
                    {
                      file: 'app/_sidebar-layout.products/bulk-import/$id.tsx',
                      method: 'backToBulkImportList',
                      infoMessage: 'unable to navigate to /app/products/bulk-import',
                    },
                    trackError
                  );
                });
              }}
            />
            <p className="font-display-4 pb-0">{t('backToBulkImportList')}</p>
          </div>
          <Button
            variant="accent"
            size="lg"
            loading={loadingExport}
            className="w-fit"
            onClick={function () {
              if (bulkGroup == null) {
                return;
              }
              setLoadingExport(true);
              exportBulkGroupToCsv(bulkGroup._id, bulkGroup.title)
                .catch(function (err) {
                  setLoadingExport(false);
                  captureException(
                    err,
                    {
                      file: 'app/_sidebar-layout.products/bulk-import/$id.tsx',
                      method: 'exportBulkGroupToCsv',
                      infoMessage: 'unable to export bulk group',
                    },
                    trackError
                  );
                })
                .finally(function () {
                  setLoadingExport(false);
                });
            }}
          >
            {tGlobal('export')}
          </Button>
        </div>
        <div className="mx-auto mt-10 flex h-full w-full max-w-content flex-col items-center">
          <div className="mb-10 flex w-full items-end justify-between">
            <div>
              <p className="font-display-2 mb-1">{bulkGroup?.title}</p>
              <div className="flex items-center gap-2">
                <Badge>
                  {bulkGroup?.language.toUpperCase()}, {getCountryName(bulkGroup?.country ?? '')}
                </Badge>
                <Badge color="gray">
                  {bulkGroup?.numberOfProducts} {t('products')}
                </Badge>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <DropdownMenu
                disabled={products.length === 0}
                position="start"
                trigger={
                  <Button
                    variant="outline"
                    size="sm"
                    isDiv
                    rightIcon={<ChevronDown width={16} height={16} />}
                  >
                    {tGlobal('sortBy')}
                  </Button>
                }
                contentClassName="min-w-[100px]"
                triggerClassName="min-w-[100px] flex-shrink-0"
              >
                {SortByDropdownMenuItems.map(function (item, index) {
                  return (
                    <DropdownMenuOption
                      key={index}
                      onClick={function () {
                        item.onClick();
                      }}
                      mainText={item.label}
                      rightElement={
                        sortByDir === item.value ? <Check width={16} height={16} /> : null
                      }
                    />
                  );
                })}
              </DropdownMenu>
              <TextField
                className="max-w-[200px]"
                iconLeft={<SearchSm width={16} height={16} />}
                placeholder={tGlobal('searchProducts')}
                value={searchInput}
                onChange={function (e) {
                  setSearchInput(e.target.value);
                }}
              />
            </div>
          </div>
          <SingleProductListing
            products={products}
            updateProductStatus={updateProductStatus}
            authorList={authorsList}
            handleDeleteSelected={handleDeleteSelected}
            handleUpdateInBulk={handleUpdateInBulk}
          />

          {totalPages > 1 ? (
            <Pagination
              currentPage={page}
              totalPages={totalPages}
              onPageChange={handlePageChange}
              className="py-8"
            />
          ) : null}
        </div>
      </div>
    </>
  );
}
